import { useCallback, useEffect, useMemo } from 'react'
import { useHistory } from 'react-router-dom'
import useQueryString from '@cannect/hooks/useQueryString'
import { SUBSCRIPTION_DETAIL_PATH } from '@cannect/services/resources/recurrency/constants'
import type { SubscriptionDocumentType } from '@cannect/services/resources/recurrency/requests'
import type { SubscriptionView } from '@cannect/services/resources/recurrency/types'

// Links dos e-mails (research R4, contracts/ui-contract.md §Endereços).
export type DeepLinkTarget = { type: 'documents'; doc: SubscriptionDocumentType } | { type: 'card' }

const DOC_PARAM: Record<string, SubscriptionDocumentType> = { receita: 'prescription', anvisa: 'anvisa' }
const DOC_PARAM_REVERSE: Record<SubscriptionDocumentType, string> = { prescription: 'receita', anvisa: 'anvisa' }

export const deepLinkSuffix = (target: DeepLinkTarget) =>
  target.type === 'card' ? '&acao=cartao' : `&documento=${DOC_PARAM_REVERSE[target.doc]}`

const useDeepLinkTarget = (): DeepLinkTarget | null => {
  const { filters } = useQueryString()
  const documento = String(filters.documento || '')
  const acao = String(filters.acao || '')

  return useMemo(() => {
    if (DOC_PARAM[documento]) return { type: 'documents', doc: DOC_PARAM[documento] }
    if (acao === 'cartao') return { type: 'card' }
    return null
  }, [documento, acao])
}

const isOpenHolder = (sub: SubscriptionView) => (sub.status === 'active' || sub.status === 'paused') && !sub.isDependent

// Lista: uma única assinatura aberta do titular → vai direto ao detalhe;
// várias → os cards mostram o atalho em destaque.
export const useListDeepLink = (subscriptions?: SubscriptionView[]) => {
  const history = useHistory()
  const target = useDeepLinkTarget()

  const holders = useMemo(() => (subscriptions || []).filter(isOpenHolder), [subscriptions])

  useEffect(() => {
    if (!target || !subscriptions || holders.length !== 1) return
    history.replace(SUBSCRIPTION_DETAIL_PATH(holders[0].id) + deepLinkSuffix(target))
  }, [target, subscriptions, holders, history])

  return { highlight: target && holders.length > 1 ? target : null }
}

// Detalhe: devolve o alvo do link e `consume()`, que limpa os parâmetros da URL.
export const useDetailDeepLink = (id: number) => {
  const history = useHistory()
  const target = useDeepLinkTarget()

  const consume = useCallback(() => {
    history.replace(SUBSCRIPTION_DETAIL_PATH(id))
  }, [history, id])

  return { target, consume }
}
